import React, { useState } from "react";
import { Modal } from "risa-oasis-ui_v2";
import { ModalId } from "../../../enums/modalId";
import { NarAgentGridBulkToggleAction } from "../../../pages/configurations/pages/narAgentGrid/utils/narAgentGridBulkToggleActions";
import WarningIcon from "../../../svg/warningIcon";

export interface NarAgentGridBulkToggleModalMeta {
  action: NarAgentGridBulkToggleAction;
  selectedCount: number;
}

interface NarAgentGridBulkToggleModalProps {
  onClose: () => void;
  loading?: boolean;
  metaData?: NarAgentGridBulkToggleModalMeta;
  handleSave: (remarks: string) => void | Promise<void>;
}

const getActionDescription = (
  action: NarAgentGridBulkToggleAction | undefined,
): string => {
  switch (action) {
    case NarAgentGridBulkToggleAction.AgentMode:
      return "On save, Agent Mode will be toggled for the selected rows.";
    case NarAgentGridBulkToggleAction.AutoPassReviewMode:
      return "On save, Autopass Review will be toggled for the selected rows.";
    case NarAgentGridBulkToggleAction.Both:
      return "On save, Agent Mode and Autopass Review will be toggled for the selected rows.";
    default:
      return "";
  }
};

/**
 * Confirmation dialog for bulk toggling Agent Mode / Autopass Review on selected NAR Agent Grid rows.
 */
const NarAgentGridBulkToggleModal: React.FC<
  NarAgentGridBulkToggleModalProps
> = ({ onClose, metaData, handleSave, loading = false }) => {
  const [reason, setReason] = useState("");
  const selectedCount = metaData?.selectedCount ?? 0;
  const actionDescription = getActionDescription(metaData?.action);

  return (
    <Modal
      dialogId={ModalId.NAR_AGENT_GRID_BULK_TOGGLE_MODAL}
      title="Bulk Toggle"
      onCancel={onClose}
      onClose={onClose}
      onSave={() => handleSave(reason.trim())}
      cancelText="Cancel"
      saveButtonText={loading ? "Confirming..." : "Confirm"}
      disableSave={!reason.trim() || loading}
    >
      <div className="nar-agent-grid-bulk-toggle-modal flex flex-col gap-4">
        <div className="nar-agent-grid-bulk-toggle-modal__warning flex items-center gap-2 rounded-md border border-secondaryYellow-2 bg-secondaryYellow-11 p-2">
          <WarningIcon height="20" width="20" />
          <div className="nar-agent-grid-bulk-toggle-modal__warning-text ml-4 flex flex-col text-xs">
            <div>
              This will affect{" "}
              <span className="font-bold">{selectedCount} row(s).</span>
            </div>
            {actionDescription && <div>{actionDescription}</div>}
          </div>
        </div>
        <div className="comments-section w-full">
          <div className="comments-section header mb-2 text-small font-normal text-primaryGray-2">
            Please provide the reason for this bulk toggle.
          </div>
          <div className="comments-section comments-textarea">
            <textarea
              className="h-20 w-full rounded-md border border-gray-300 p-2 text-tiny font-normal"
              placeholder=""
              value={reason}
              disabled={loading}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default NarAgentGridBulkToggleModal;
